import { useEffect, useRef, useState } from 'react';
import { consumeContinuation, flushPlayback, playbackRecord, recordPlayback, resumeProgress } from '../utils/episodePlayback';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import { fancyTimeFormat } from './timeHelper';
import { loadYouTubeIframeAPI } from './youtubeHelper';
import { clearYouTubeProgress, getYouTubeProgress, saveYouTubeProgress } from './youtubeProgress';
import AudioVideoContinuation from './AudioVideoContinuation';
import './YouTubeVideo.css';

const VideoPlayback = ({ videoId, userId, track, autoPlay, initialSeconds, audioContinuationSeconds,
    nativeFullscreen, onPlaybackState, progressOwnerRef }) => {
    const containerRef = useRef(null);
    const stateCallback = useRef(onPlaybackState);
    stateCallback.current = onPlaybackState;
    const playFromRef = useRef(null);
    const retryStartRef = useRef(null);
    const [attempt, setAttempt] = useState(0);
    const [failed, setFailed] = useState(false);
    const [ready, setReady] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [buffering, setBuffering] = useState(false);
    const [continuationDismissed, setContinuationDismissed] = useState(false);
    const [matchesVideo, setMatchesVideo] = useState(true);
    const [seconds, setSeconds] = useState(() => getYouTubeProgress(userId, videoId));

    useEffect(() => {
        let active = true;
        let player;
        let progressInterval;
        let hasPlayed = false;
        let observedPositiveTime = false;
        let ended = false;
        let isReady = false;
        let isPlaying = false;
        let pendingSeek = null;
        let activityAt = null;
        let lastRemoteSync = 0;
        let lastSavedSnapshot;
        let wakeLock;
        let wakeLockPending = false;
        const history = playbackRecord(userId, track);
        const stored = history.videoProgress;
        const requestedStart = attempt > 0 ? retryStartRef.current : initialSeconds;
        const startSeconds = history.deletedAt ? 0 : Number.isFinite(requestedStart) ? requestedStart
            : stored?.videoId === videoId ? resumeProgress(stored) : getYouTubeProgress(userId, videoId);
        const audio = document.getElementById('audio');
        setFailed(false);
        setReady(false);
        setPlaying(false);
        setBuffering(false);
        setMatchesVideo(true);
        setSeconds(startSeconds);

        const ownsProgress = () => progressOwnerRef.current === (userId || null);
        const stopObserving = () => window.clearInterval(progressInterval);
        const reportState = state => stateCallback.current?.(state);
        reportState('loading');
        const sync = () => flushPlayback(userId, ownsProgress);
        const releaseSentinel = (sentinel) => {
            try {
                Promise.resolve(sentinel?.release()).catch(() => {});
            } catch {
                // An expired platform lock needs no further cleanup.
            }
        };
        const releaseWakeLock = () => {
            const previous = wakeLock;
            wakeLock = null;
            releaseSentinel(previous);
        };
        const requestWakeLock = async () => {
            if (!active || !isPlaying || document.hidden || wakeLock || wakeLockPending || !navigator.wakeLock?.request) return;
            wakeLockPending = true;
            try {
                const sentinel = await navigator.wakeLock.request('screen');
                if (!active || !ownsProgress() || !isPlaying || document.hidden) {
                    releaseSentinel(sentinel);
                    return;
                }
                wakeLock = sentinel;
                sentinel.addEventListener('release', () => {
                    if (wakeLock === sentinel) wakeLock = null;
                }, { once: true });
            } catch {
                // Playback remains available when the browser or power settings deny a lock.
            } finally {
                wakeLockPending = false;
            }
        };
        const isRequestedVideo = () => {
            try {
                return new URL(player?.getVideoUrl()).searchParams.get('v') === videoId;
            } catch {
                return false;
            }
        };
        const saveProgress = (allowRestart = false, completion = false) => {
            if (!ownsProgress() || !hasPlayed || ended || !isRequestedVideo()) return;
            try {
                const currentSeconds = player.getCurrentTime();
                if (pendingSeek !== null) {
                    const elapsed = (Date.now() - pendingSeek.requestedAt) / 1000;
                    const rate = player.getPlaybackRate?.() || 1;
                    // YouTube can briefly report the old position after a requested seek.
                    if (elapsed < 5 && Math.abs(currentSeconds - pendingSeek.seconds) > elapsed * Math.max(1, rate) + 1) return;
                    pendingSeek = null;
                }
                if (currentSeconds === 0 && (!allowRestart || !observedPositiveTime)) return;
                const now = Date.now() / 1000;
                const snapshot = `${currentSeconds}:${player.getDuration()}:${completion}:${isPlaying}`;
                // Paused polling detects scrubs without uploading the same bookmark every second.
                if (!isPlaying && !activityAt && snapshot === lastSavedSnapshot) return;
                const saved = recordPlayback(userId, track, 'video', { seconds: completion ? player.getDuration() : currentSeconds,
                    durationSeconds: player.getDuration(), videoId, completed: completion,
                    actualPlayedAtSeconds: activityAt || (isPlaying ? now : undefined) }, now);
                if (saved && (!isPlaying || completion || now - lastRemoteSync >= 10)) { sync(); lastRemoteSync = now; }
                if (saved) activityAt = null;
                if (saved || !track) lastSavedSnapshot = snapshot;
                if ((!track || saved) && saveYouTubeProgress(userId, videoId, completion ? 0 : currentSeconds, player.getDuration())) {
                    if (currentSeconds > 0) observedPositiveTime = true;
                    if (active) setSeconds(currentSeconds);
                }
            } catch {
                // The iframe may disappear during navigation or report unavailable media.
            }
        };
        const observePausedPosition = () => {
            if (active && hasPlayed && !ended && !document.hidden) {
                // Paused scrubbing does not always emit another YouTube state event.
                progressInterval = window.setInterval(() => saveProgress(true), 1000);
            }
        };
        const pausePlayback = () => {
            if (!active) return;
            isPlaying = false;
            saveProgress();
            reportState('paused');
            setPlaying(false);
            releaseWakeLock();
            try {
                player?.pauseVideo?.();
            } catch {
                // Leaving the page may already have removed the iframe.
            }
            stopObserving();
        };
        const onVisibilityChange = () => {
            if (document.hidden) pausePlayback();
            else if (active && !isPlaying) {
                stopObserving();
                observePausedPosition();
            }
        };
        const onAudioPlay = () => {
            pausePlayback();
            // An already-paused iframe may not emit another PAUSED event.
            observePausedPosition();
        };
        const fail = () => {
            if (!active) return;
            isPlaying = false;
            saveProgress();
            active = false;
            stopObserving();
            releaseWakeLock();
            window.clearTimeout(timeout);
            player?.destroy();
            player = null;
            setPlaying(false);
            setFailed(true);
            reportState('failed');
        };
        const timeout = window.setTimeout(fail, 20000);
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', pausePlayback);
        window.addEventListener('online', sync);
        audio?.addEventListener('play', onAudioPlay);

        playFromRef.current = (requestedSeconds = 0, fromAudio = false) => {
            if (!active || !isReady || !ownsProgress() || !isRequestedVideo() || !player.loadVideoById) return false;
            if (!Number.isFinite(requestedSeconds) || requestedSeconds < 0 || requestedSeconds > 2147483647) return false;
            try {
                const durationSeconds = player.getDuration() || stored?.durationSeconds;
                const targetSeconds = Number.isFinite(durationSeconds) && durationSeconds > 0
                    ? Math.min(requestedSeconds, Math.max(0, durationSeconds - 1)) : requestedSeconds;
                stopObserving();
                hasPlayed = false;
                ended = false;
                observedPositiveTime = true;
                pendingSeek = { seconds: targetSeconds, requestedAt: Date.now() };
                player.loadVideoById({ videoId, startSeconds: targetSeconds });
                // An explicit seek is a checkpoint even if the user leaves
                // before YouTube emits PLAYING. It must not reorder Played.
                if (recordPlayback(userId, track, 'video', { seconds: targetSeconds, durationSeconds, videoId, resetsCompletion: true })) sync();
                activityAt = null;
                if (targetSeconds === 0) clearYouTubeProgress(userId, videoId);
                else saveYouTubeProgress(userId, videoId, targetSeconds, durationSeconds);
                if (fromAudio && track?.id) consumeContinuation(userId, track, videoId, true);
                setSeconds(targetSeconds);
                setContinuationDismissed(true);
                return true;
            } catch {
                fail();
                return false;
            }
        };

        loadYouTubeIframeAPI().then((YT) => {
            if (!active) return;
            // YouTube replaces this node; it must not be a React-owned child.
            const mount = document.createElement('div');
            containerRef.current.appendChild(mount);
            player = new YT.Player(mount, {
                width: '100%',
                height: '100%',
                videoId,
                playerVars: {
                    autoplay: autoPlay ? 1 : 0,
                    start: Math.floor(startSeconds),
                    playsinline: 1,
                    fs: nativeFullscreen ? 1 : 0,
                    origin: window.location.origin,
                },
                events: {
                    onReady: () => {
                        if (!active) return;
                        window.clearTimeout(timeout);
                        isReady = true;
                        setReady(true);
                        reportState('ready');
                        if (autoPlay) player?.playVideo?.();
                    },
                    onStateChange: ({ data }) => {
                        if (!active || !ownsProgress()) return;
                        stopObserving();
                        const requestedVideo = isRequestedVideo();
                        setMatchesVideo(requestedVideo);
                        if (data === YT.PlayerState.PLAYING) {
                            setBuffering(false);
                            isPlaying = true;
                            activityAt = Date.now() / 1000;
                            reportState('playing');
                            if (requestedVideo) {
                                hasPlayed = true;
                                ended = false;
                                saveProgress();
                            }
                            if (document.hidden) {
                                pausePlayback();
                                return;
                            }
                            isPlaying = true;
                            setPlaying(true);
                            audio?.pause();
                            requestWakeLock();
                            if (requestedVideo) progressInterval = window.setInterval(saveProgress, 5000);
                        } else if (data === YT.PlayerState.PAUSED || data === YT.PlayerState.ENDED) {
                            setBuffering(false);
                            isPlaying = false;
                            setPlaying(false);
                            reportState(data === YT.PlayerState.ENDED ? 'ended' : 'paused');
                            releaseWakeLock();
                            if (data === YT.PlayerState.ENDED && hasPlayed && requestedVideo) {
                                saveProgress(true, true);
                                ended = true;
                                clearYouTubeProgress(userId, videoId);
                                setSeconds(0);
                            } else if (data === YT.PlayerState.PAUSED) {
                                saveProgress(true);
                                observePausedPosition();
                            }
                        } else {
                            setBuffering(true);
                            isPlaying = false;
                            reportState('buffering');
                        }
                    },
                    onAutoplayBlocked: () => {
                        reportState('paused');
                        if (!active || !ownsProgress()) return;
                        isPlaying = false;
                        setBuffering(false);
                        setPlaying(false);
                        releaseWakeLock();
                        stopObserving();
                        saveProgress(true);
                        observePausedPosition();
                    },
                    onError: fail,
                },
            });
            const iframe = player.getIframe();
            iframe.title = 'Episode video on YouTube';
            iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
            iframe.allowFullscreen = nativeFullscreen;
            iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        }).catch(fail);

        return () => {
            active = false;
            isPlaying = false;
            saveProgress(true);
            stopObserving();
            releaseWakeLock();
            window.clearTimeout(timeout);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', pausePlayback);
            window.removeEventListener('online', sync);
            audio?.removeEventListener('play', onAudioPlay);
            playFromRef.current = null;
            player?.destroy();
        };
    }, [videoId, userId, track, autoPlay, initialSeconds, nativeFullscreen, progressOwnerRef, attempt]);

    const showsResume = seconds >= 1 && !playing && !failed && matchesVideo;
    const fallbackSeconds = Math.floor(seconds);
    const showsContinuation = Number.isFinite(audioContinuationSeconds) && audioContinuationSeconds >= 0 &&
        audioContinuationSeconds <= 2147483647 && ready && !failed && matchesVideo && !continuationDismissed;

    return (
        <div className='youtubeVideoScreen'>
            <div className='youtubeVideoLayout'>
                {showsContinuation && <div className='audioVideoContinuationPosition'>
                    <AudioVideoContinuation seconds={audioContinuationSeconds} paused={buffering}
                        onContinue={() => playFromRef.current?.(audioContinuationSeconds, true)}
                        onDismiss={() => setContinuationDismissed(true)} />
                </div>}
                <div className={`youtubeVideoFrame${failed ? ' youtubeVideoFrameFailed' : ''}`}>
                    {failed ? (
                        <div className='youtubeVideoError' role='status'>
                            <p>This video can’t play here.</p>
                            <button type='button' onClick={() => {
                                retryStartRef.current = seconds;
                                setAttempt(value => value + 1);
                            }}>Try again</button>
                            <a href={`https://www.youtube.com/watch?v=${videoId}${fallbackSeconds ? `&t=${fallbackSeconds}s` : ''}`}
                                target='_blank' rel='noopener noreferrer' onClick={() => document.getElementById('audio')?.pause()}>
                                Open in YouTube
                            </a>
                        </div>
                    ) : <>
                        <div ref={containerRef} className='youtubeVideoEmbed' aria-hidden={!ready} inert={!ready ? '' : undefined}
                            style={{ visibility: ready ? 'visible' : 'hidden' }} />
                        {!ready && <div className='youtubeVideoPlaceholder' role='status' aria-label='Loading video'>
                            <span className='youtubeVideoSpinner' aria-hidden='true' />
                        </div>}
                    </>}
                </div>
                <div className='youtubeVideoResume' aria-hidden={!showsResume} style={{ visibility: showsResume ? 'visible' : 'hidden' }}>
                    <span className='youtubeVideoResumeTime'><HistoryRounded aria-hidden='true' />Resumes at {fancyTimeFormat(seconds)}</span>
                    <button type='button' disabled={!ready || !showsResume} onClick={() => playFromRef.current?.()}>Start over</button>
                </div>
            </div>
        </div>
    );
};

export default function YouTubeVideoPlayer({ videoId, userId, track, autoPlay = false, initialSeconds,
    audioContinuationSeconds, nativeFullscreen = true, onPlaybackState }) {
    const progressOwnerRef = useRef(userId || null);
    progressOwnerRef.current = userId || null;
    return <VideoPlayback key={JSON.stringify([userId || null, track?.id || null, videoId])}
        videoId={videoId} userId={userId} track={track} autoPlay={autoPlay} initialSeconds={initialSeconds}
        audioContinuationSeconds={audioContinuationSeconds} nativeFullscreen={nativeFullscreen}
        onPlaybackState={onPlaybackState} progressOwnerRef={progressOwnerRef} />;
}
